import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Scale, Mail, Lock, ArrowRight, Check, X, User, Info } from 'lucide-react';
import { api } from '../lib/api';

const passwordRules = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'uppercase', label: 'At least 1 uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'At least 1 lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'At least 1 number', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'At least 1 special character (!@#$%^&*)', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) },
];

const usernameRules = [
  { id: 'length', label: '3-20 characters', test: (u) => u.length >= 3 && u.length <= 20 },
  { id: 'chars', label: 'Only letters, numbers, and underscores', test: (u) => /^[a-zA-Z0-9_]+$/.test(u) },
];

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') || '';
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationToken, setVerificationToken] = useState(null);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const validatePassword = () => {
    return passwordRules.every((rule) => rule.test(password));
  };

  const validateUsername = () => {
    if (!username || username.trim() === '') return false; // Username is required
    return usernameRules.every((rule) => rule.test(username));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !username || !password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!validateUsername()) {
      toast.error('Username does not meet requirements');
      return;
    }

    if (!validatePassword()) {
      toast.error('Password does not meet requirements');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/signup', {
        email,
        password,
        confirm_password: confirmPassword,
        username: username.trim() || null
      });
      
      if (response.data.requires_verification) {
        setVerificationToken(response.data.email); // Store email instead of token
        setRegisteredEmail(response.data.email);
        if (response.data.email_sent) {
          toast.success('Account created! Check your email for verification link.');
        } else {
          toast.success('Account created! Verification email may take a moment to arrive.');
        }
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        detail.forEach((err) => toast.error(err.msg || err));
      } else {
        toast.error(detail || 'Failed to create account');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-secondary/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
            <Scale className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-bold text-foreground tracking-tight">
            Fund<span className="text-primary">fair</span>
          </span>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1 text-center pb-4">
            <CardTitle className="text-2xl">
              {verificationToken ? 'Verify Your Email' : 'Create an account'}
            </CardTitle>
            <CardDescription>
              {verificationToken 
                ? `We've sent a verification link to ${registeredEmail}`
                : 'Join Fundfair and start making a difference'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {verificationToken ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-green-800 mb-2">Check Your Email!</h3>
                  <p className="text-sm text-green-700 mb-4">
                    We've sent a verification link to <strong>{registeredEmail}</strong>. 
                    Click the link in the email to verify your account.
                  </p>
                  <p className="text-xs text-green-600">
                    Didn't receive the email? Check your spam folder or click below to resend.
                  </p>
                </div>
                
                <Button 
                  variant="outline"
                  className="w-full h-12 rounded-xl"
                  onClick={async () => {
                    try {
                      await api.post('/auth/resend-verification', { email: registeredEmail });
                      toast.success('Verification email resent!');
                    } catch (error) {
                      toast.error('Failed to resend email');
                    }
                  }}
                >
                  Resend Verification Email
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setVerificationToken(null);
                      setRegisteredEmail('');
                      setEmail('');
                      setUsername('');
                      setPassword('');
                      setConfirmPassword('');
                    }}
                    className="text-sm text-muted-foreground hover:text-foreground"
                  >
                    Use a different email
                  </button>
                </div>
              </div>
            ) : (
              <>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 h-12 rounded-xl"
                        data-testid="signup-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="username">Username <span className="text-red-500">*</span></Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="username"
                        type="text"
                        placeholder="choose_a_username"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="pl-10 h-12 rounded-xl"
                        data-testid="signup-username-input"
                        required
                      />
                    </div>
                    
                    {/* Privacy notice */}
                    <div className="flex items-start gap-2 p-2 bg-blue-50 rounded-lg text-sm text-blue-700">
                      <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>This name will be visible to everyone. You do not need to use your real name.</span>
                    </div>

                    {/* Username requirements - always show */}
                    <div className="mt-2 p-3 bg-secondary/50 rounded-xl space-y-1.5 animate-fade-in">
                      {usernameRules.map((rule) => {
                        const passed = username ? rule.test(username) : false;
                        return (
                          <div
                            key={rule.id}
                            className={`flex items-center gap-2 text-sm ${
                              passed ? 'text-green-600' : 'text-muted-foreground'
                            }`}
                            data-testid={`username-rule-${rule.id}`}
                          >
                            {passed ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                              {rule.label}
                            </div>
                          );
                        })}
                      </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl"
                        data-testid="signup-password-input"
                      />
                    </div>

                    {/* Password requirements */}
                    {password && (
                      <div className="mt-3 p-3 bg-secondary/50 rounded-xl space-y-1.5 animate-fade-in">
                        {passwordRules.map((rule) => {
                          const passed = rule.test(password);
                          return (
                            <div
                              key={rule.id}
                              className={`flex items-center gap-2 text-sm ${
                                passed ? 'text-green-600' : 'text-muted-foreground'
                              }`}
                              data-testid={`password-rule-${rule.id}`}
                            >
                              {passed ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                <X className="w-4 h-4" />
                              )}
                              {rule.label}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 h-12 rounded-xl"
                        data-testid="signup-confirm-password-input"
                      />
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="text-sm text-destructive flex items-center gap-1 mt-1">
                        <X className="w-4 h-4" />
                        Passwords do not match
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl text-base font-semibold btn-bounce"
                    disabled={loading}
                    data-testid="signup-submit-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Create Account
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{' '}
                    <Link
                      to={nextPath ? `/login?next=${encodeURIComponent(nextPath)}` : '/login'}
                      className="text-primary font-medium hover:underline"
                      data-testid="login-link"
                    >
                      Sign in
                    </Link>
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Collect funds. Upload receipts. Repeat.
        </p>
      </div>
    </div>
  );
}
